import React, { useContext, useMemo } from 'react';

// DEPENDENCIES
import { compose } from 'redux';
import { useFormikContext } from 'formik';
import isNumber from 'lodash/isNumber';
import sumBy from 'lodash/sumBy';
import trim from 'lodash/trim';

// HELPERS
import { withRouter } from 'helpers/withRouter';

// GLOBAL FUNCTIONS
import { formatDollar } from 'functions.js';

// GLOBAL CONTEXT
import { RecordContext } from 'helpers/getRecordData';

// GLOBAL COMPONENTS
import Form from 'core/form/Form';
import Card from 'parts/card/Card';
import Sprite from 'core/tools/Sprite';
import Tooltip from 'core/tools/Tooltip';

// LOCAL COMPONENTS
import InputAppendCol from 'routers/interface/sections/parts/InputAppendCol';

const InternalDealForm = compose(withRouter)(() => {
  // CONTEXT
  const { record: { inventory, ...record } = {} } = useContext(RecordContext);
  const { values } = useFormikContext() || {};

  // MEMOS
  const {
    aryRebate = [],
    aryDealerIncentive = [],
    aryDealerPack = [],
    aryOtherItem = [],
    aryDownPayment = [],
    aryOweItem = [],
    aryBankFee = [],
    aryTrade,
    lease_gross_split,
    lease_rate_reserve,
    lease_warranty_reserve,
    selling_price,
    cost,
    sale_type_id
  } = useMemo(() => values || {}, [values]);
  const { am_gross, fi_gross } = useMemo(() => record || {}, [record]);

  const saleTypeId = useMemo(() => parseInt(sale_type_id) === 2, [sale_type_id]);
  const purchaseType = useMemo(
    () => (parseInt(sale_type_id) === 1 ? 'Purchase' : parseInt(sale_type_id) === 2 ? 'Lease' : ''),
    [sale_type_id]
  );

  const adjustedRebate = useMemo(() => {
    return (aryRebate || []).filter(a => trim(a.item_Amount) !== '' && !a.isDeleted);
  }, [aryRebate]);

  const adjustedDealerIncentive = useMemo(() => {
    return (aryDealerIncentive || []).filter(a => trim(a.item_Amount) !== '' && !a.isDeleted);
  }, [aryDealerIncentive]);

  const adjustedDealerPack = useMemo(() => {
    return (aryDealerPack || []).filter(a => trim(a.item_Amount) !== '' && !a.isDeleted);
  }, [aryDealerPack]);

  const adjustedOtherItem = useMemo(() => {
    return (aryOtherItem || []).filter(a => trim(a.other_item_cost) !== '' && !a.isDeleted);
  }, [aryOtherItem]);

  const adjustedDownPayment = useMemo(() => {
    return (aryDownPayment || []).filter(a => !a.isDeleted);
  }, [aryDownPayment]);

  const adjustedOweItem = useMemo(() => {
    return (aryOweItem || []).filter(a => trim(a.item_price) !== '' && !a.isDeleted);
  }, [aryOweItem]);

  const adjustedBankFee = useMemo(() => {
    return (aryBankFee || []).filter(a => trim(a.item_amount) !== '' && !a.isDeleted);
  }, [aryBankFee]);

  const TOTAL_TRADE_ACV = useMemo(() => {
    return sumBy(aryTrade || [], p => +parseFloat(p?.acv || 0));
  }, [aryTrade]);

  const TOTAL_TRADE_ALLOWANCE = useMemo(() => {
    return sumBy(aryTrade || [], p => +parseFloat(p?.trade_in_allowance || 0));
  }, [aryTrade]);

  const TOTAL_OTHER_ITEM = useMemo(() => {
    return sumBy(adjustedOtherItem || [], p => +parseFloat(p?.other_item_cost || 0));
  }, [adjustedOtherItem]);

  const TOTAL_OWE_ITEM = useMemo(() => {
    return sumBy(adjustedOweItem || [], p => +parseFloat(p?.item_price || 0));
  }, [adjustedOweItem]);

  const TOTAL_DEALER_INCENTIVE = useMemo(() => {
    return sumBy(adjustedDealerIncentive || [], p => +parseFloat(p?.item_Amount || 0));
  }, [adjustedDealerIncentive]);

  const TOTAL_BANK_FEE = useMemo(() => {
    return sumBy(adjustedBankFee || [], p => +parseFloat(p?.item_amount || 0));
  }, [adjustedBankFee]);

  const TOTAL_CREDIT_CARD = useMemo(() => {
    return sumBy(adjustedDownPayment?.filter(p => p.dp_type === 1) || [], p => +parseFloat(p?.dp_Amount || 0));
  }, [adjustedDownPayment]);

  const TOTAL_CHECK = useMemo(() => {
    return sumBy(adjustedDownPayment?.filter(p => p.dp_type === 2) || [], p => +parseFloat(p?.dp_Amount || 0));
  }, [adjustedDownPayment]);

  const TOTAL_CASH = useMemo(() => {
    return sumBy(adjustedDownPayment?.filter(p => p.dp_type === 3) || [], p => +parseFloat(p?.dp_Amount || 0));
  }, [adjustedDownPayment]);

  const TOTAL_PROMISSORY_NOTE = useMemo(() => {
    return sumBy(adjustedDownPayment?.filter(p => p.dp_type === 4) || [], p => +parseFloat(p?.dp_Amount || 0));
  }, [adjustedDownPayment]);

  const TOTAL_WIRE = useMemo(() => {
    return sumBy(adjustedDownPayment?.filter(p => p.dp_type === 5) || [], p => +parseFloat(p?.dp_Amount || 0));
  }, [adjustedDownPayment]);

  const TOTAL_PRODUCT_CANCELLATION_FUNDS = useMemo(() => {
    return sumBy(adjustedDownPayment?.filter(p => p.dp_type === 6) || [], p => +parseFloat(p?.dp_Amount || 0));
  }, [adjustedDownPayment]);

  const TOTAL_FE_DEALER_PACK = useMemo(() => {
    return sumBy(adjustedDealerPack || [], p => +parseFloat(p?.item_Amount || 0));
  }, [adjustedDealerPack]);

  const TOTAL_DP = useMemo(() => {
    return (
      +TOTAL_CREDIT_CARD +
      +TOTAL_CHECK +
      +TOTAL_CASH +
      +TOTAL_PROMISSORY_NOTE +
      +TOTAL_WIRE +
      +TOTAL_PRODUCT_CANCELLATION_FUNDS
    );
  }, [TOTAL_CREDIT_CARD, TOTAL_CHECK, TOTAL_CASH, TOTAL_PROMISSORY_NOTE, TOTAL_WIRE, TOTAL_PRODUCT_CANCELLATION_FUNDS]);

  const FRONTEND_GROSS_BEFORE_PACK = useMemo(() => {
    return (
      +TOTAL_DEALER_INCENTIVE +
      +(selling_price || 0) +
      +(lease_rate_reserve || 0) +
      +(lease_warranty_reserve || 0) +
      +TOTAL_TRADE_ACV -
      +TOTAL_TRADE_ALLOWANCE -
      +(cost || 0) -
      +TOTAL_OTHER_ITEM -
      +TOTAL_OWE_ITEM -
      +TOTAL_BANK_FEE
    );
  }, [
    selling_price,
    cost,
    TOTAL_TRADE_ACV,
    TOTAL_TRADE_ALLOWANCE,
    TOTAL_OTHER_ITEM,
    lease_rate_reserve,
    lease_warranty_reserve,
    TOTAL_OWE_ITEM,
    TOTAL_BANK_FEE,
    TOTAL_DEALER_INCENTIVE
  ]);

  const FRONTEND_GROSS_BP_AFTER_LEASE_SPLIT = useMemo(() => {
    return +FRONTEND_GROSS_BEFORE_PACK * +(+(100 - +lease_gross_split) / 100);
  }, [FRONTEND_GROSS_BEFORE_PACK, lease_gross_split]);

  const FI_GROSS_LEASE_SPLIT = useMemo(() => {
    return +FRONTEND_GROSS_BEFORE_PACK * +(lease_gross_split / 100);
  }, [FRONTEND_GROSS_BEFORE_PACK, lease_gross_split]);

  const FRONTEND_GROSS_AFTER_PACK = useMemo(() => {
    return +(saleTypeId ? FRONTEND_GROSS_BP_AFTER_LEASE_SPLIT : FRONTEND_GROSS_BEFORE_PACK) - +TOTAL_FE_DEALER_PACK;
  }, [FRONTEND_GROSS_BP_AFTER_LEASE_SPLIT, FRONTEND_GROSS_BEFORE_PACK, saleTypeId, TOTAL_FE_DEALER_PACK]);

  const TOTAL_FI_GROSS = useMemo(() => {
    return +(fi_gross || 0) + +(saleTypeId ? FI_GROSS_LEASE_SPLIT : 0);
  }, [fi_gross, FI_GROSS_LEASE_SPLIT, saleTypeId]);

  const TOTAL_BACKEND_GROSS = useMemo(() => {
    return +(am_gross || 0) + +TOTAL_FI_GROSS;
  }, [am_gross, TOTAL_FI_GROSS]);

  const TOTAL_FE_BE_GROSS_BEFORE_PACK = useMemo(() => {
    return +(saleTypeId ? FRONTEND_GROSS_BP_AFTER_LEASE_SPLIT : FRONTEND_GROSS_BEFORE_PACK) + +TOTAL_BACKEND_GROSS;
  }, [FRONTEND_GROSS_BEFORE_PACK, saleTypeId, FRONTEND_GROSS_BP_AFTER_LEASE_SPLIT, TOTAL_BACKEND_GROSS]);

  const TOTAL_FE_BE_GROSS_AFTER_PACK = useMemo(() => {
    return +FRONTEND_GROSS_AFTER_PACK + TOTAL_BACKEND_GROSS;
  }, [FRONTEND_GROSS_AFTER_PACK, TOTAL_BACKEND_GROSS]);

  // RENDER
  return (
    <Form.Body>
      <Form.PlainText label="Purchase Type" value={purchaseType} />
      <Form.PlainText label="Selling Price" value={selling_price} schema="dollars" />
      {saleTypeId && (
        <>
          <Form.PlainText label="Lease Rate Reserve" value={`+ ${formatDollar(lease_rate_reserve || 0)}`} />
          <Form.PlainText
            label="Lease Warranty Reserve"
            value={`+ ${formatDollar(lease_warranty_reserve || 0)}`}
          />
        </>
      )}
      {adjustedRebate.map(
        (v, i) => (
          <Form.PlainText
            key={`${v.sales_rebate_id}-${i}`}
            label={`Rebate ${i + 1}`}
            value={v.item_Amount}
            schema="dollars"
            input={{
              append: <InputAppendCol>{v.item_name} {v.item_code}</InputAppendCol>
            }}
          />
        )
      )}
      {aryTrade?.length ?
        aryTrade.map((v, i) => (
          <Form.PlainText
            key={`${v?.trade_id}-${i}`}
            label={`Trade ${i + 1} ACV`}
            value={`+ ${formatDollar(v?.acv)}`}
          />
        )) : null}
      {aryTrade?.length ?
        aryTrade.map((v, i) => (
          <Form.PlainText
            key={`${v?.trade_id}--${i + 1}`}
            label={`Trade ${i + 1} Allowance`}
            value={`- ${formatDollar(v?.trade_in_allowance)}`}
          />
        )) : null}
      <Form.PlainText
        label="Cost"
        value={`${cost ? `- ${formatDollar(cost)}` : ''} `}
        input={{
          append:
            (isNumber(inventory?.invoice_cost) && cost < inventory?.invoice_cost) ||
            (isNumber(inventory?.dms_invoice_price) && cost < inventory?.invoice_cost) ? (
              <Tooltip tip="Warning: Cost entered is less than DMS Invoice and/or Invoice Cost">
                <Sprite use="warning" fill="danger" size="lg" />
              </Tooltip>
            ) : null
        }}
      />
      {adjustedOweItem.map(
        (v, i) => (
          <Form.PlainText
            key={`${v.sales_bank_fee_id}-${i}`}
            label={`We Owe ${i + 1}`}
            value={`- ${formatDollar(v.item_price)}`}
            input={{
              append: <InputAppendCol>{v.item_name}</InputAppendCol>
            }}
          />
        )
      )}
      {adjustedOtherItem.map(
        (v, i) => (
          <Form.PlainText
            key={`${v.sales_other_id}-${i}`}
            label={`Other Item ${i + 1}`}
            value={`- ${formatDollar(v.other_item_cost)}`}
            input={{
              append: <InputAppendCol>{v.other_item}</InputAppendCol>
            }}
          />
        )
      )}
      {adjustedBankFee.map(
        (v, i) => (
          <Form.PlainText
            key={`${v.sales_other_id}-${i}`}
            label={`Bank Fee ${i + 1}`}
            value={`- ${formatDollar(v.item_amount)}`}
            input={{
              append: <InputAppendCol>{v.item_name}</InputAppendCol>
            }}
          />
        )
      )}
      {adjustedDealerIncentive.map(
        (v, i) => (
          <Form.PlainText
            key={`${v.sales_dealer_incentive_id}-${i}`}
            label={`Dealer Incentive ${i + 1}`}
            value={`+ ${formatDollar(v.item_Amount)}`}
            input={{
              append: <InputAppendCol>{v.item_name} {v.item_code}</InputAppendCol>
            }}
          />
        )
      )}
      <Form.PlainText
        label="Frontend Gross Before Pack"
        className="fw-bolder"
        value={FRONTEND_GROSS_BEFORE_PACK}
        schema="dollars"
      />
      {saleTypeId && (
        <>
          <Form.PlainText label="Lease Split (F&amp;I %)" name="lease_gross_split" schema="percent" />
          <Form.PlainText
            label="Frontend Gross BP After Lease Split"
            className="fw-bolder"
            value={FRONTEND_GROSS_BP_AFTER_LEASE_SPLIT}
            schema="dollars"
          />
        </>
      )}
      {adjustedDealerPack.map(
        (v, i) => (
          <Form.PlainText
            key={`${v.sales_dealer_pack_id}-${i}`}
            label={`FE Dealer Pack ${i + 1}`}
            value={`- ${formatDollar(v.item_Amount || 0)}`}
            input={{
              append: <InputAppendCol>{v.item_name} {v.item_code}</InputAppendCol>
            }}
          />
        )
      )}
      {TOTAL_CREDIT_CARD && <Form.PlainText label="Total Credit Card" value={TOTAL_CREDIT_CARD} schema="dollars" />}
      {TOTAL_CHECK && <Form.PlainText label="Total Check" value={TOTAL_CHECK} schema="dollars" />}
      {TOTAL_CASH && <Form.PlainText label="Total Cash" value={TOTAL_CASH} schema="dollars" />}
      {TOTAL_PROMISSORY_NOTE && (
        <Form.PlainText label="Total Promissory Note" value={TOTAL_PROMISSORY_NOTE} schema="dollars" />
      )}
      {TOTAL_WIRE && <Form.PlainText label="Total Wire" value={TOTAL_WIRE} schema="dollars" />}
      {TOTAL_PRODUCT_CANCELLATION_FUNDS && (
        <Form.PlainText
          label="Total Product Cancellation Funds"
          value={TOTAL_PRODUCT_CANCELLATION_FUNDS}
          schema="dollars"
        />
      )}
      {TOTAL_DP && <Form.PlainText label="Grand Total DP" value={TOTAL_DP} schema="dollars" />}
      <Form.PlainText
        label="Frontend Gross After Pack"
        className="fw-bolder"
        value={FRONTEND_GROSS_AFTER_PACK}
        schema="dollars"
      />
      <Form.PlainText label="AM Gross" value={am_gross} schema="dollars" />
      <Form.PlainText label="F&amp;I Gross" value={fi_gross} schema="dollars" />
      {saleTypeId && (
        <Form.PlainText label="F&amp;I Gross (Lease Split)" value={FI_GROSS_LEASE_SPLIT} schema="dollars" />
      )}
      <Form.PlainText label="Total F&amp;I Gross" value={TOTAL_FI_GROSS} schema="dollars" />
      <Form.PlainText
        label="Total Backend Gross(AM + F&amp;I)"
        className="fw-bolder"
        value={TOTAL_BACKEND_GROSS}
        schema="dollars"
      />
      <Form.PlainText label="Total FE &amp; BE Gross Before Pack" value={TOTAL_FE_BE_GROSS_BEFORE_PACK} schema="dollars" />
      <Form.PlainText label="Total FE &amp; BE Gross After Pack" value={TOTAL_FE_BE_GROSS_AFTER_PACK} schema="dollars" />
    </Form.Body>
  );
});

// EXPORT
export default function InternalDealRecap(props) {
  return <Card title="Internal Deal Recap" body={<InternalDealForm {...props} />} />;
}
